import { addMinutes, differenceInMinutes, format, parseISO } from 'date-fns';
import { useState, type FormEvent } from 'react';

import { Button, Dropdown, Field, Input } from '@/design-system';
import type { Order, ScheduleInput } from '@/entities/order';
import { InspectorPicker } from '@/shared/ui';

import { inspectionsCopy } from '../copy';
import { DURATIONS, localMoment, toApiMoment } from '../moments';

export interface ScheduleFormProps {
    order: Order;
    saving?: boolean;
    error?: string | null;
    onSubmit: (input: ScheduleInput) => void;
    onCancel: () => void;
}

/**
 * Inspecteur, dag, starttijd en duur: meer is een afspraak niet. Een bestaande
 * afspraak staat alvast ingevuld, zodat verplaatsen één wijziging is. De
 * inspecteur komt uit de gedeelde inspecteurskeuze, met Mijzelf bovenaan.
 */
export function ScheduleForm({
    order,
    saving = false,
    error = null,
    onSubmit,
    onCancel,
}: ScheduleFormProps) {
    const start = order.scheduledAt ? parseISO(order.scheduledAt) : null;
    const end = order.scheduledEndAt ? parseISO(order.scheduledEndAt) : null;
    const knownDuration = start && end ? differenceInMinutes(end, start) : 120;

    const [assigneeUuid, setAssigneeUuid] = useState(order.assignee?.uuid ?? '');
    const [date, setDate] = useState(start ? format(start, 'yyyy-MM-dd') : '');
    const [time, setTime] = useState(start ? format(start, 'HH:mm') : '09:00');
    const [duration, setDuration] = useState(
        (DURATIONS as readonly number[]).includes(knownDuration) ? knownDuration : 120,
    );
    const [touched, setTouched] = useState(false);

    const complete = assigneeUuid !== '' && date !== '' && time !== '';

    function submit(event: FormEvent) {
        event.preventDefault();
        setTouched(true);

        if (!complete) {
            return;
        }

        const moment = localMoment(date, time);

        onSubmit({
            scheduledAt: toApiMoment(moment),
            scheduledEndAt: toApiMoment(addMinutes(moment, duration)),
            assigneeUuid,
        });
    }

    return (
        <form onSubmit={submit} className="grid gap-3">
            <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                {inspectionsCopy.scheduleIntro(order.property.shortAddress)}
            </p>

            <Field label={inspectionsCopy.scheduleInspector} size="sm">
                <InspectorPicker value={assigneeUuid} onChange={setAssigneeUuid} />
            </Field>

            <div className="grid grid-cols-2 gap-3">
                <Field label={inspectionsCopy.scheduleDate} size="sm">
                    <Input
                        type="date"
                        value={date}
                        onChange={(event) => setDate(event.target.value)}
                    />
                </Field>
                <Field label={inspectionsCopy.scheduleTime} size="sm">
                    <Input
                        type="time"
                        step={900}
                        value={time}
                        onChange={(event) => setTime(event.target.value)}
                    />
                </Field>
            </div>

            <Field label={inspectionsCopy.scheduleDuration} size="sm">
                <Dropdown
                    value={String(duration)}
                    options={DURATIONS.map((minutes) => ({
                        value: String(minutes),
                        label: inspectionsCopy.durationOption(minutes),
                    }))}
                    onChange={(minutes) => setDuration(Number(minutes))}
                />
            </Field>

            {touched && !complete ? (
                <p role="alert" className="m-0 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {inspectionsCopy.scheduleMissing}
                </p>
            ) : null}

            {error ? (
                <p role="alert" className="m-0 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {error}
                </p>
            ) : null}

            <div className="flex flex-wrap gap-2">
                <Button type="submit" variant="primary" size="md" disabled={saving}>
                    {inspectionsCopy.scheduleSave}
                </Button>
                <Button variant="outline" size="md" onClick={onCancel}>
                    {inspectionsCopy.scheduleCancel}
                </Button>
            </div>
        </form>
    );
}
